/**
 * One chain module's section body in /admin/workshop (#683): the decisions the
 * module declares (admin.controls, "live", then its admin.actions, then
 * "prepare" — always visible, #1213), its
 * screens and timing (nothing else), this Kuutio's facilitation content
 * (#1082), then the module's own facilitator
 * panel (the catalog's admin.section). Information known before the Kuutio is
 * entered on Sisältö (#747); a missing input links there, and the panel waits
 * until every required input exists, since it works on that data.
 */
import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { resolveAdminSection } from './workshopModuleRegistry';
import ModuleSettingsPanel from './ModuleSettingsPanel';
import FacilitationPanel from './FacilitationPanel';
import ModuleControls from './ModuleControls';
import ModuleActionsShelf from './ModuleActionsShelf';
import ModuleStateLine from './ModuleStateLine';

const Blocked = styled.div`
  padding: 16px 18px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
`;

const Empty = styled.div`
  padding: 20px 4px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  strong { display: block; color: rgba(255, 255, 255, 0.75); margin-bottom: 4px; }
`;

function label(obj, language) {
  return obj?.labels?.[language] || obj?.labels?.fi || obj?.id || '';
}

/**
 * @param {{ step: object, lookups: object, instanceId: string, locked?: boolean,
 *   scenarios?: object[], hasDeck?: boolean, onSave: Function, onChanged?: Function,
 *   onEditScreens?: Function }} props
 */
export default function ModuleAdminContent({
  step, lookups, instanceId, locked = false, scenarios = [], hasDeck = false,
  onSave, onChanged, onEditScreens,
}) {
  const { t, language } = useTranslation();
  const def = lookups.modules[step.moduleId];
  const Section = resolveAdminSection(def?.admin?.section);
  const missing = (step.inputs || []).filter((i) => i.status === 'manual_needed');

  let body;
  if (missing.length) {
    body = (
      <Blocked>
        {t('workshopOrchestrator.blockedUntil', {
          items: missing.map((i) => label(lookups.handoffs[i.handoff], language)).join(', '),
        })}
        {' '}
        <Link to={`/admin/content?instance=${instanceId}#tieto-${missing[0].handoff}`}>
          {t('workshopOrchestrator.enterOnContent')}
        </Link>
      </Blocked>
    );
  } else if (Section) {
    body = <Section instanceId={instanceId} locked={locked} scenarios={scenarios} step={step} />;
  } else if (!(def?.admin?.controls || []).length) {
    body = (
      <Empty>
        <strong>{t('workshopOrchestrator.noSettingsTitle')}</strong>
        {t('workshopOrchestrator.noSettingsBody')}
      </Empty>
    );
  }

  return (
    <>
      {/* Tila: output present, checks met (#1218) */}
      <ModuleStateLine instanceId={instanceId} step={step} checks={def?.admin?.checks} />
      <ModuleControls controls={def?.admin?.controls} when="live" instanceId={instanceId} reached={Boolean(step.reachedAt)} />
      {/* The module's own actions, whether or not the step is live (#1213) */}
      <ModuleActionsShelf instanceId={instanceId} stepId={step.id} actions={def?.admin?.actions} />
      <ModuleControls controls={def?.admin?.controls} when="prepare" instanceId={instanceId} reached={Boolean(step.reachedAt)} />
      <ModuleSettingsPanel
        step={step}
        locked={locked}
        hasDeck={hasDeck}
        onSave={(patch) => onSave(step.stepId, patch)}
        onEditScreens={onEditScreens}
      />
      <FacilitationPanel step={step} instanceId={instanceId} locked={locked} onSave={(patch) => onSave(step.stepId, patch)} />
      {body}
    </>
  );
}
